/**
 * trackResolver — maps the current hostname to an evaluation "track" (vertical).
 *
 * A track is one subdomain product: upsc.markszen.com, college.markszen.com, …
 * This is the frontend mirror of the backend whitelist (config/tracks.js).
 * Adding a vertical = one entry here + the backend entry + rubric data + a
 * Netlify/DNS alias. No routing-logic changes.
 *
 * @module utils/trackResolver
 */

/** Known tracks, keyed by subdomain label. Keep in sync with backend config/tracks.js. */
export const TRACKS = {
  upsc: { track: 'upsc', label: 'UPSC' },
  // college: { track: 'college', label: 'College' },  // future — pure config
};

/**
 * Resolve a track config from a hostname.
 * @param {string} hostname - e.g. 'upsc.markszen.com'
 * @returns {{track: string, label: string} | null} null for the main app / unknown subdomains
 */
export function resolveTrackFromHostname(hostname) {
  if (!hostname || typeof hostname !== 'string') return null;
  const sub = hostname.split('.')[0].toLowerCase();
  return TRACKS[sub] || null;
}

/** The track for the current window, or null when running the main app. */
export function getCurrentTrack() {
  if (typeof window === 'undefined') return null;
  return resolveTrackFromHostname(window.location.hostname);
}

/** Convenience: the current track slug ('upsc') or null. */
export function getCurrentTrackSlug() {
  return getCurrentTrack()?.track || null;
}

/**
 * True when the user is in the UPSC product context — either on the UPSC
 * subdomain (incl. the MarksZen UPSC app, which loads upsc.markszen.com) or on
 * a /upsc/* path. Used by auth guards to route logged-out/expired users to
 * /upsc/signin instead of the main MarksZen login.
 * @returns {boolean}
 */
export function isUpscContext() {
  if (typeof window === 'undefined') return false;
  if (getCurrentTrackSlug() === 'upsc') return true;
  return window.location.pathname.startsWith('/upsc');
}

export default { TRACKS, resolveTrackFromHostname, getCurrentTrack, getCurrentTrackSlug, isUpscContext };
